import React, {useState} from 'react';
import Modal from 'react-modal';
import styles from './ModifyTaxInvoicePopUp.module.css';
import Button from '../../../components/common/button/Button';
import {useAlert} from "../../../components/common/error/AlertContext";
import {UpdateTaxInvoiceRequest} from "../../../types/admin";
import {updateTaxInvoiceAPI} from "../../../services/admin";

interface ModifyTaxInvoicePopUpProps {
    isOpen: boolean;
    onClose: () => void;
    taxInvoiceInfo: UpdateTaxInvoiceRequest;
}

//세금계산서 수정 팝업
const ModifyTaxInvoicePopUp: React.FC<ModifyTaxInvoicePopUpProps> = ({
                                                                         isOpen,
                                                                         onClose,
                                                                         taxInvoiceInfo
                                                                     }) => {
    const [requestData, setRequestData] = useState<UpdateTaxInvoiceRequest>(taxInvoiceInfo);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {showAlert} = useAlert();

    //이메일 형식 확인
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    //전화번호 형식 확인
    const isValidPhoneNumber = (phone: string) => {
        // 한국 휴대폰 번호 패턴: 010-1234-5678, 010.1234.5678, 01012345678
        const phoneRegex = /^(010|070|011|016|017|018|019)[-.]?\d{3,4}[-.]?\d{4}$/;
        return phoneRegex.test(phone);
    };

    //전화번호 형식화 (자동으로 하이픈 추가)
    const formatPhoneNumber = (phone: string) => {
        // 숫자만 추출
        const numbers = phone.replace(/[^\d]/g, '');

        // 010으로 시작하는 경우
        if (numbers.startsWith('010') || numbers.startsWith('070')) {
            if (numbers.length <= 3) return numbers;
            if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
        }

        // 기타 번호 (011, 016, 017, 018, 019)
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    //전화번호 유효성 검사
    const validatePhoneNumber = (phone: string) => {
        if (!phone.trim()) {
            return '전화번호를 입력해주세요.';
        }

        const numbers = phone.replace(/[^\d]/g, '');

        // 길이 검사
        if (numbers.length < 10 || numbers.length > 11) {
            return '전화번호는 10-11자리 숫자여야 합니다.';
        }

        // 유효한 시작 번호 검사
        if (!['010', '011', '016', '017', '018', '019', '070'].some(prefix => numbers.startsWith(prefix))) {
            return '유효하지 않은 전화번호 형식입니다.';
        }

        // 전체 형식 검사
        if (!isValidPhoneNumber(phone)) {
            return '올바른 전화번호 형식이 아닙니다.';
        }

        return null;
    };

    //전화번호 입력 핸들러 (자동 포맷팅 포함)
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedPhone = formatPhoneNumber(e.target.value);
        setRequestData(prev => ({
            ...prev,
            managerPhone: formattedPhone
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isLoading) return;

        //입력 필수값 확인
        if (requestData.email === "") {
            showAlert('담당자 이메일을 입력해주세요.', "error");
            return;
        }

        // 이메일 형식 검사
        if (!isValidEmail(requestData.email)) {
            showAlert('올바른 이메일 형식이 아닙니다.', "error");
            return;
        }

        if (requestData.managerName === "") {
            showAlert('담당자 성명을 입력해주세요.', "error");
            return;
        }

        if (requestData.managerPhone === "") {
            showAlert('담당자 번호를 입력해주세요.', "error");
            return;
        }

        // 전화번호 유효성 검사
        const phoneError = validatePhoneNumber(requestData.managerPhone);
        if (phoneError) {
            showAlert(phoneError, "error");
            return;
        }

        if (requestData.supplyCost === 0) {
            showAlert('공급가액을 입력해주세요.', "error");
            return;
        }

        if (requestData.tax === 0) {
            showAlert('세액을 입력해주세요.', "error");
            return;
        }

        setIsLoading(true);
        try {
            // 서버에는 전화번호 숫자만 전송
            const submitData = {
                ...requestData,
                managerPhone: requestData.managerPhone.replace(/[^\d]/g, '')
            };
            await updateTaxInvoiceAPI(submitData);
            onClose();
            showAlert('세금계산서가 수정되었습니다.', "info");
        } catch (error) {
            console.error('failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                <div className={styles.modalContent}>
                    <div className={styles.header}>
                        <h2>세금계산서 수정</h2>
                        <button onClick={onClose} className={styles.closeButton} type="button" aria-label="Close">×
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label>담당자 이메일</label>
                            <input
                                type="email"
                                value={requestData.email}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setRequestData(prev => ({
                                        ...prev,
                                        email: value
                                    }));
                                }}
                                placeholder="example@company.com"
                                className={styles.input}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>담당자 성명</label>
                            <input
                                type="text"
                                value={requestData.managerName}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setRequestData(prev => ({
                                        ...prev,
                                        managerName: value
                                    }));
                                }}
                                placeholder="홍길동"
                                className={styles.input}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>담당자 전화번호</label>
                            <input
                                type="text"
                                value={requestData.managerPhone}
                                onChange={handlePhoneChange}
                                placeholder="010-1234-5678"
                                className={styles.input}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>공급가액</label>
                            <input
                                type="number"
                                value={requestData.supplyCost === 0 ? '' : requestData.supplyCost}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    setRequestData(prev => ({
                                        ...prev,
                                        supplyCost: value
                                    }));
                                }}
                                min="0"
                                placeholder="1000000"
                                className={styles.input}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>세액</label>
                            <input
                                type="number"
                                value={requestData.tax === 0 ? '' : requestData.tax}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    setRequestData(prev => ({
                                        ...prev,
                                        tax: value
                                    }));
                                }}
                                min="0"
                                placeholder="100000"
                                className={styles.input}
                                disabled={isLoading}
                            />
                        </div>

                        {/*요청 생성 버튼*/}
                        <Button
                            type="submit"
                            variant="primary"
                            size="large"
                            fullWidth
                            disabled={isLoading}
                        >
                            수정
                        </Button>
                    </form>
                </div>
            </Modal>
        </>
    );
};

export default ModifyTaxInvoicePopUp;